import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { adoptCurrentSettings } from '../../lib/mutations/personalSettings';
import { elowenClient } from '../../lib/elowenClient';
import { QUERY_KEYS } from '../../lib/queries';
import { VOICE_DICTATION_PAUSE_SECONDS } from '../../lib/voice';
import type { UserVoiceSettings, UserVoiceSettingsPatch, UserVoiceSettingsSnapshot, VoiceCatalog } from '../../lib/types';

const modelSchema = z.object({ id: z.string().min(1), voices: z.array(z.string().min(1)) });
const catalogSchema = z.object({
  providers: z.array(z.object({ id: z.string().min(1), label: z.string() })),
  callModels: z.array(modelSchema), transcriptionModels: z.array(modelSchema), dictationModels: z.array(modelSchema), speechModels: z.array(modelSchema),
  active: z.object({ providerId: z.string().min(1), callModel: z.string(), speechModel: z.string(), voices: z.array(z.string().min(1)) }).nullable(),
}) satisfies z.ZodType<VoiceCatalog>;
const settingsSchema = z.object({
  voice: z.string().min(1).max(100), language: z.string().max(100), callback: z.boolean(), dictationPauseSeconds: z.union([z.null(), ...VOICE_DICTATION_PAUSE_SECONDS.map(seconds => z.literal(seconds))]),
  revision: z.number().int().nonnegative(),
}) satisfies z.ZodType<UserVoiceSettingsSnapshot>;

export const voicePreferencesKey = ['my-voice-settings'] as const;
export const voicePreferencesQuery = { queryKey: voicePreferencesKey, queryFn: async () => settingsSchema.parse(await elowenClient.myVoiceSettings()) };

export function useVoiceCatalog() {
  return useQuery({ queryKey: QUERY_KEYS.voiceCatalog, queryFn: async () => catalogSchema.parse(await elowenClient.voiceCatalog()) });
}

export function useVoicePreferences() {
  return useQuery(voicePreferencesQuery);
}

async function saveVoicePreferences(patch: UserVoiceSettingsPatch): Promise<UserVoiceSettingsSnapshot> {
  return settingsSchema.parse(await elowenClient.saveMyVoiceSettings(patch));
}

/** Personal voice saves share the cached revision with every caller and retain server conflict state. */
export function useSaveMyVoicePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<UserVoiceSettings>) => {
      const current = qc.getQueryData<UserVoiceSettingsSnapshot>(voicePreferencesKey);
      if (!current) throw new Error('Voice preferences have not loaded');
      return saveVoicePreferences({ ...patch, expectedRevision: current.revision });
    },
    onSuccess: (saved) => qc.setQueryData(voicePreferencesKey, saved),
    onError: (error) => {
      adoptCurrentSettings(qc, voicePreferencesKey, error);
    },
  });
}
